import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SendtooLogo from "@/components/shared/SendtooLogo";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { sendForgotPasswordOtp, completeForgotPasswordReset } = useAuth();

  const [step, setStep] = useState("identifier"); // identifier | reset
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendForgotPasswordOtp(identifier.trim());
      toast.success("If an account exists for this identifier, an OTP has been sent.");
      setStep("reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await sendForgotPasswordOtp(identifier.trim());
      toast.success("OTP resent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend OTP.");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const user = await completeForgotPasswordReset(identifier.trim(), otp.trim(), newPassword, confirmPassword);
      toast.success("Password reset successfully. Welcome back!");
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "agent") navigate("/agent");
      else if (user.role === "staff") navigate("/staff");
      else navigate("/customer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password. Check your OTP and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex justify-center">
          <SendtooLogo size="lg" align="center" />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-brand-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Forgot Password</h1>
          </div>

          {step === "identifier" && (
            <>
              <p className="text-slate-500 text-sm mb-6">
                Enter your email (admin) or phone number (staff) and we'll send an OTP to your registered mobile.
              </p>
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email or Phone Number</label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Email or phone number"
                    required
                    autoFocus
                    data-testid="forgot-identifier"
                    className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-send-otp"
                  className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 uppercase tracking-wide"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>
              </form>
            </>
          )}

          {step === "reset" && (
            <>
              <p className="text-slate-500 text-sm mb-6">
                Enter the OTP sent to your registered phone, along with your new password.
              </p>
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">OTP</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="6-digit OTP"
                    required
                    autoFocus
                    data-testid="forgot-otp"
                    className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm text-center tracking-widest font-mono focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      data-testid="forgot-new-password"
                      className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
                    />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm New Password</label>
                  <input
                    type={showPwd ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your new password"
                    required
                    data-testid="forgot-confirm-password"
                    className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-reset-submit"
                  className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 uppercase tracking-wide"
                >
                  {loading ? "Resetting..." : "Reset Password & Sign In"}
                </button>
                <button type="button" onClick={handleResendOtp} className="w-full text-xs text-slate-500 hover:text-brand-500">
                  Didn't get an OTP? Resend
                </button>
              </form>
            </>
          )}

          <p className="text-center text-xs text-slate-400 mt-6">
            <Link to="/auth/login" className="hover:text-slate-600">← Back to Sign In</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
